import { ModificationAction, ModificationHandler } from '@core/types';
import { TextModificationHandler } from './TextModificationHandler';
import { VisibilityModificationHandler } from './VisibilityModificationHandler';
import { ImageModificationHandler } from './ImageModificationHandler';
import { ModificationError } from '@shared/errors';

export class ModificationRegistry {
  private handlers = new Map<ModificationAction, ModificationHandler>();

  constructor() {
    this.registerDefaultHandlers();
  }

  private registerDefaultHandlers(): void {
    this.register(ModificationAction.SET_TEXT, new TextModificationHandler());
    this.register(ModificationAction.HIDE, new VisibilityModificationHandler());
    this.register(ModificationAction.REPLACE_IMAGE, new ImageModificationHandler());
  }

  register(action: ModificationAction, handler: ModificationHandler): void {
    this.handlers.set(action, handler);
  }

  apply(modification: any, element: Element): void {
    const handler = this.handlers.get(modification.action);
    if (!handler) {
      throw new ModificationError(
        `No handler registered for action: ${modification.action}`,
        modification.action,
      );
    }

    if (handler.validate && !handler.validate(modification)) {
      throw new ModificationError(
        `Invalid modification for action: ${modification.action}`,
        modification.action,
      );
    }

    try {
      handler.apply(element, modification);
    } catch (error) {
      throw new ModificationError(
        `Failed to apply modification: ${error instanceof Error ? error.message : 'Unknown error'}`,
        modification.action,
        error,
      );
    }
  }

  getHandler(action: ModificationAction): ModificationHandler | undefined {
    return this.handlers.get(action);
  }
}

// Export individual handlers for direct use
export { TextModificationHandler, VisibilityModificationHandler, ImageModificationHandler };
